import { useEffect, useRef, useState } from 'react';
import { fastapi } from '../../services/api';
import BtnTaratic from '../ui/BtnTaratic';

export default function AudioMessage({ url, messageId, index, onError }: {
  url: string; messageId: string; index: number; onError: () => void;
}) {
  const audio = useRef<HTMLAudioElement>(null);
  const worker = useRef<Worker | null>(null);
  const request = useRef<AbortController | null>(null);
  const [speed, setSpeed] = useState(1);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [transcript, setTranscript] = useState<string | null>(null);
  const stop = () => { request.current?.abort(); worker.current?.terminate(); worker.current = null; };
  useEffect(() => () => stop(), []);

  const transcribe = async () => {
    stop();
    const controller = new AbortController();
    request.current = controller;
    setBusy(true); setStatus('Descargando audio…');
    try {
      const { data } = await fastapi.get<ArrayBuffer>('/chat/message-audio', {
        params: { message_id: messageId, attachment_index: index },
        responseType: 'arraybuffer', signal: controller.signal, timeout: 60000,
      });
      if (controller.signal.aborted) return;
      const context = new AudioContext();
      let decoded: AudioBuffer;
      try { decoded = await context.decodeAudioData(data); } finally { await context.close(); }
      if (decoded.duration > 300) throw new Error('La transcripción admite audios de hasta 5 minutos.');
      const offline = new OfflineAudioContext(1, Math.max(1, Math.ceil(decoded.duration * 16000)), 16000);
      const source = offline.createBufferSource(); source.buffer = decoded;
      source.connect(offline.destination); source.start();
      const samples = (await offline.startRendering()).getChannelData(0);
      if (controller.signal.aborted) return;
      setStatus('Descargando modelo de voz (solo la primera vez)…');
      const task = new Worker(new URL('./transcription.worker.ts', import.meta.url), { type: 'module' });
      worker.current = task;
      task.onmessage = ({ data: result }) => {
        if (result.status === 'loading') setStatus(`Cargando modelo de voz${typeof result.progress === 'number' ? `: ${Math.round(result.progress)}%` : '…'}`);
        if (result.status === 'transcribing') setStatus('Convirtiendo audio a texto…');
        if (result.status === 'done') {
          setTranscript(result.text.trim() || 'No se detectó voz.'); setStatus(''); setBusy(false); stop();
        }
        if (result.status === 'error') { setStatus('No se pudo transcribir. Comprueba la conexión y vuelve a intentarlo.'); setBusy(false); stop(); }
      };
      task.onerror = () => { setStatus('La transcripción no está disponible en este navegador.'); setBusy(false); stop(); };
      task.postMessage(samples, [samples.buffer]);
    } catch (error) {
      if (!controller.signal.aborted) {
        setStatus(error instanceof Error && error.message.startsWith('La transcripción') ? error.message : 'No se pudo leer el audio. Puede haber caducado o no ser compatible.');
        setBusy(false);
      }
    }
  };

  return <div className="min-w-0 space-y-2">
    <audio ref={audio} controls preload="none" src={url} onError={onError} className="w-full max-w-full" />
    <div className="flex flex-wrap items-center gap-2">
      <BtnTaratic type="button" size="xs" aria-label={`Velocidad de reproducción: ${speed}×`} onClick={() => {
        const next = speed === 2 ? 1 : speed + 0.5; setSpeed(next); if (audio.current) audio.current.playbackRate = next;
      }}>{speed}×</BtnTaratic>
      <BtnTaratic type="button" size="xs" disabled={busy || transcript !== null} onClick={transcribe}>Convertir a texto</BtnTaratic>
      {busy && <BtnTaratic color="ghost-dark" type="button" size="xs" onClick={() => { stop(); setBusy(false); setStatus('Transcripción cancelada.'); }}>Cancelar</BtnTaratic>}
    </div>
    {status && <p role="status" className="text-xs">{status}</p>}
    {transcript !== null && <div className="whitespace-pre-wrap break-words text-sm" aria-label="Transcripción"><p className="mb-1 text-xs opacity-70">Transcripción automática · puede contener errores</p>{transcript}</div>}
  </div>;
}
